import {
    Autocomplete,
    Checkbox,
    Grid,
    IconButton,
    TextField,
    Typography,
} from '@mui/material';
import { DataGrid, esES } from '@mui/x-data-grid';
import { DataGridStyle } from '../../../styles/DataGridSytle';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { sendRequest } from '../../../services/request';
import { numericFormatter } from 'react-number-format';
import { CheckBox, CheckBoxOutlineBlank, DoneAll } from '@mui/icons-material';
import dayjs from 'dayjs';
import 'dayjs/locale/es';

export const RutaEntrega = ({ pais }) => {
    const URL = process.env.REACT_APP_API_URL;
    const [rows, setRows] = useState([]);
    const [limit, setLimit] = useState(10);
    const [page, setPage] = useState(0);
    const [rowCount, setRowCount] = useState(null);

    //Condicionales
    const [loading, setLoading] = useState(true);
    const [recargar, setRecargar] = useState(false);
    const today = useMemo(() => new Date(), []);

    const [tarjetonesSelected, setTarjetonesSelected] = useState([]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const res = await sendRequest(
                    'get',
                    `${URL}/${pais}/envio/ruta?page=${page}&limit=${limit}&empresa=Mensajero Local&fecha=${
                        today.toISOString().split('T')[0]
                    }`,
                );
                setRows(res.data.items);
                setRowCount(res.data.meta.totalItems);
                setLoading(false);
            } catch (error) {
                console.log(error);
            }
        };
        fetchData();
    }, [URL, pais, page, limit, recargar, today]);

    const columns = [
        {
            field: 'item',
            headerName: 'Item',
            flex: 'auto',
            headerClassName: 'header',
            sortable: false,
            headerAlign: 'center',
            align: 'center',
            /* renderCell: ({ row }) => row.cantidad, */
        },
        {
            field: 'producto',
            headerName: 'Producto',
            flex: 1,
            headerClassName: 'header',
            sortable: false,
            headerAlign: 'center',
            align: 'center',
            renderCell: ({ row }) => row.campana?.nombre,
        },
        {
            field: 'numero_tarjeton',
            headerName: 'Código',
            flex: 1.5,
            headerClassName: 'header',
            sortable: false,
            editable: true,
            headerAlign: 'center',
            align: 'center',
            renderCell: ({ row }) => {
                return (
                    <Autocomplete
                        disabled={row.estado.id === 9 || row.cantidad <= 0}
                        multiple
                        disableCloseOnSelect
                        disablePortal
                        fullWidth
                        value={row?.tarjetones}
                        options={row?.campana.tarjetones}
                        getOptionLabel={(option) => option.numero}
                        getOptionDisabled={(option) =>
                            tarjetonesSelected.length >= row.cantidad
                        }
                        size="small"
                        onChange={(event, newValue) =>
                            //handleChangeTarjeton(newValue, row)
                            setTarjetonesSelected(newValue)
                        }
                        renderOption={(props, option, { selected }) => (
                            <li {...props} key={option.id}>
                                <Checkbox
                                    sx={{
                                        ml: -1,
                                    }}
                                    icon={
                                        <CheckBoxOutlineBlank fontSize="small" />
                                    }
                                    checkedIcon={<CheckBox fontSize="small" />}
                                    checked={selected}
                                />
                                {option.numero}
                            </li>
                        )}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder=""
                                variant="standard"
                            />
                        )}
                    ></Autocomplete>
                );
            },
        },
        {
            field: 'cantidad',
            headerName: 'Cantidad',
            flex: 1,
            headerClassName: 'header',
            sortable: false,
            headerAlign: 'center',
            align: 'center',
            /* renderCell: ({ row }) => row.cantidad, */
        },
        {
            field: 'cliente',
            headerName: 'Cliente',
            flex: 1,
            headerClassName: 'header',
            sortable: false,
            headerAlign: 'center',
            align: 'center',
            renderCell: ({ row }) =>
                `${row.cliente?.nombres} ${row.cliente?.apellidos}`,
        },
        {
            field: 'celular',
            headerName: 'Celular',
            flex: 1,
            headerClassName: 'header',
            sortable: false,
            headerAlign: 'center',
            align: 'center',
            renderCell: ({ row }) => row.cliente?.celular,
        },
        {
            field: 'destino',
            headerName: 'Destino',
            flex: 1,
            headerClassName: 'header',
            sortable: false,
            headerAlign: 'center',
            align: 'center',
            renderCell: ({ row }) => row.cliente?.direccion_exacta,
        },
        {
            field: 'medio_pago',
            headerName: 'Medio de Pago',
            flex: 'auto',
            headerClassName: 'header',
            sortable: false,
            headerAlign: 'center',
            align: 'center',
            renderCell: ({ row }) => row.medio_pago?.nombre,
        },
        {
            field: 'valor',
            headerName: 'A cobrar',
            flex: 'auto',
            headerClassName: 'header',
            sortable: false,
            headerAlign: 'center',
            align: 'center',
            renderCell: ({ row }) => {
                if (row.cantidad === 1)
                    return numericFormatter(
                        (
                            row.campana?.precio_tarjeton * row.cantidad +
                            row.campana?.costo_delivery
                        ).toString(),
                        {
                            thousandSeparator: true,
                            prefix: pais === 'colombia' ? '$' : 'B/. ',
                            decimalScale: 2,
                            fixedDecimalScale: true,
                        },
                    );
                if (row.cantidad > 1)
                    return numericFormatter(
                        (
                            row.campana?.precio_tarjeton * row.cantidad
                        ).toString(),
                        {
                            thousandSeparator: true,
                            prefix: pais === 'colombia' ? '$' : 'B/. ',
                            decimalScale: 2,
                            fixedDecimalScale: true,
                        },
                    );
            },
        },
        {
            field: 'acciones',
            type: 'actions',
            cellClassName: 'actions',
            headerName: 'Acciones',
            headerClassName: 'header',
            flex: 1.2,
            getActions: ({ row }) => {
                return [
                    <IconButton
                        disabled={row.estado.id === 9}
                        color="primary"
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            borderRadius: 0,
                        }}
                        onClick={() => handleConfirmarEntrega(row)}
                    >
                        <DoneAll />
                        <Typography>
                            {row.campana?.division?.id === 1
                                ? 'Confirmar Entrega'
                                : 'Confirmar Donación '}
                        </Typography>
                    </IconButton>,
                ];
            },
        },
    ];

    const handlePaginationChange = (event) => {
        setPage(event.page);
        setLimit(event.pageSize);
    };

    const handleRowUpdate = useCallback(async (newRow, originalRow) => {}, []);

    const handleRowUpdateError = (error) => {
        console.log(error);
    };

    const handleConfirmarEntrega = async (envio) => {
        try {
            await sendRequest('put', `${URL}/${pais}/envio/${envio?.id}`, {
                ...envio,
                fecha_envio: dayjs(new Date()).format('YYYY-MM-DD HH:MM:ss'),
                estado: {
                    id: 9,
                    estado: 'Afiliado',
                },
                confirmado: true,
            });
            handleChangeTarjeton(tarjetonesSelected, envio);
            setRecargar(!recargar);
        } catch (error) {
            console.log(error);
        }
    };

    const handleChangeTarjeton = async (newValue, envio) => {
        try {
            for (const tarjeton of newValue) {
                await sendRequest(
                    'patch',
                    `${URL}/${pais}/tarjeton/${tarjeton.id}`,
                    {
                        ...tarjeton,
                        envio: envio.id,
                    },
                );
            }
            //setRecargar(!recargar);
        } catch (error) {
            console.log(error);
        }
    };

    return (
        <>
            <Grid contariner>
                <Grid item xs={12} md={6}>
                    <Typography
                        variant="h4"
                        color="primary"
                        sx={{
                            textAlign: 'start',
                            marginLeft: '10px',
                            marginTop: '20px',
                        }}
                    >
                        Entregas Mensajero Local - (
                        {dayjs(today).locale('es').format('dddd D')} De{' '}
                        {dayjs(today).locale('es').format('MMMM')} - Sem{' '}
                        {dayjs(today).locale('es').week()})
                    </Typography>
                </Grid>
                <Grid container alignItems="center" justifyContent="center">
                    <Grid item xs={12} md={9}>
                        <DataGrid
                            localeText={
                                esES.components.MuiDataGrid.defaultProps
                                    .localeText
                            }
                            disableColumnSelector
                            disableDensitySelector
                            disableColumnFilter
                            disableColumnMenu
                            disableRowSelectionOnClick
                            loading={loading}
                            initialState={{
                                pagination: {
                                    paginationModel: { pageSize: limit },
                                },
                            }}
                            rows={rows ? rows : []}
                            columns={columns}
                            paginationMode="server"
                            rowCount={rowCount ? rowCount : 0}
                            onPaginationModelChange={handlePaginationChange}
                            processRowUpdate={handleRowUpdate}
                            onProcessRowUpdateError={handleRowUpdateError}
                            sx={[
                                DataGridStyle,
                                {
                                    //Hace el cambio del color de los campos editables
                                    '& .MuiDataGrid-cell--editable': {
                                        border: 1,
                                        borderColor: '#ffffff',
                                        bgcolor: '#016A8225', // Los ultimos digitos son la transparencia (25%)
                                    },
                                    '& .MuiDataGrid-virtualScroller': {
                                        minHeight: '30vh',
                                    },
                                },
                            ]}
                        />
                    </Grid>
                </Grid>
            </Grid>
        </>
    );
};
